import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import {
  AchievementsIndividualForm,
  AchievementsIndividualUpcomingOrNot
} from '.';

import type { IndividualAchievementFormValues } from './AchievementsIndividualForm';

interface Props {
  updateMainViewData: () => void;
  updatePendingData: (() => void) | undefined;
}

const AchievementsIndividualAddOrRequest = ({
  updateMainViewData,
  updatePendingData
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      individualAchievementCustomAttributes,
      individualAchievementsUserRequireApproval,
      individualAchievementsUpcomingEnabled
    }
  } = useReduxState();

  const uploadFiles = useRef<Function>(null);

  const [isFutureAchievement, setIsFutureAchievement] = useState(false);
  const [view, setView] = useState<'selection' | 'form'>(
    individualAchievementsUpcomingEnabled ? 'selection' : 'form'
  );

  const initialValues = {
    comments: '',
    completionDate: null,
    customAttributes: {},
    expirationDate: null,
    startedAtDate: null,
    title: '',
    workload: 0
  } as IndividualAchievementFormValues;

  if (individualAchievementCustomAttributes?.length) {
    for (const attr of individualAchievementCustomAttributes) {
      if (attr.type === 'Number') {
        initialValues.customAttributes[attr.key] = attr.min;
      }
    }
  }

  const handleHide = () => {
    setIsFutureAchievement(false);
    setView(individualAchievementsUpcomingEnabled ? 'selection' : 'form');
  };

  const submit = async (values: IndividualAchievementFormValues) => {
    try {
      if (view === 'selection') {
        setView('form');
        return false;
      }

      const attachments = await uploadFiles.current();

      await api.post('/admin/achievements-individual/request-or-add', {
        attachments,
        comments: values.comments,
        completionDate: values.completionDate,
        customAttributes: values.customAttributes || {},
        emails: values.emails,
        expirationDate: values.expirationDate,
        startedAtDate: values.startedAtDate,
        title: values.title?.trim(),
        usernames: values.usernames,
        workload: values.workload
      });

      updateMainViewData();
      if (updatePendingData) updatePendingData();

      dispatch(
        actions.addNotice({
          text: individualAchievementsUserRequireApproval
            ? messages.achievementRequested
            : messages.achievementUsersAddSuccess,
          type: 'success'
        })
      );
      return;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const approvalRequiredText = individualAchievementsUserRequireApproval
    ? messages.achievementRequest
    : messages.achievementAdd;

  const settings =
    view === 'selection'
      ? {
          modalActionCategory: 'default',
          modalActionText: messages.commonContinue,
          modalWidth: 'small'
        }
      : {
          modalActionCategory: 'success',
          modalActionText: approvalRequiredText,
          modalWidth: 'wide'
        };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={approvalRequiredText}
      initialValues={initialValues}
      modalActionText={settings.modalActionText}
      modalActionCategory={settings.modalActionCategory}
      modalTitle={approvalRequiredText}
      modalWidth={settings.modalWidth}
      onHide={handleHide}
      onSubmit={submit}
    >
      {() => (
        <div>
          <div
            className="selection-radio"
            style={{ display: view === 'selection' ? 'block' : 'none' }}
          >
            <AchievementsIndividualUpcomingOrNot
              isFutureAchievement={isFutureAchievement}
              onSelection={setIsFutureAchievement}
            />
          </div>

          <div
            className="form"
            style={{ display: view === 'form' ? 'block' : 'none' }}
          >
            <AchievementsIndividualForm
              getUploadAttachmentsHandler={(fn) => (uploadFiles.current = fn)}
              isFutureAchievement={isFutureAchievement}
              visible={view === 'form'}
            />
          </div>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementsIndividualAddOrRequest;
